import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cx } from '../utils';
import './Modal.css';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// Ochiq/yopiqlik faqat tashqaridan boshqariladi: open + onClose.
// subtitle — sarlavha ostidagi kichik matn (ixtiyoriy).
// size: sm | md | lg. closeOnOverlayClick default false — forma ma'lumoti tasodifan yo'qolmasin.
function Modal({
  open,
  onClose,
  title,
  subtitle,
  footer,
  size = 'md',
  closeOnOverlayClick = false,
  closeLabel = 'Yopish',
  className,
  children,
  ...rest
}) {
  const dialogRef = useRef(null);
  const overlayPressRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const titleId = useId();

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;

    const dialog = dialogRef.current;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const body = dialog.querySelector('.ui-modal__body');
    const initial = (body && body.querySelector(FOCUSABLE)) || dialog.querySelector(FOCUSABLE) || dialog;
    initial.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (onCloseRef.current) onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = Array.from(dialog.querySelectorAll(FOCUSABLE));
      if (items.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  // Overlay faqat bosish ham, qo'yib yuborish ham overlay ustida bo'lsa yopadi
  // (modal ichida matn belgilab, tashqarida qo'yib yuborilganda yopilib ketmasin).
  const handleOverlayMouseDown = (event) => {
    overlayPressRef.current = event.target === event.currentTarget;
  };
  const handleOverlayClick = (event) => {
    const pressedOnOverlay = overlayPressRef.current;
    overlayPressRef.current = false;
    if (closeOnOverlayClick && pressedOnOverlay && event.target === event.currentTarget && onClose) {
      onClose();
    }
  };

  return createPortal(
    <div className="ui-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
      <div
        {...rest}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cx('ui-modal', `ui-modal--${size}`, className)}
      >
        <div className="ui-modal__header">
          {(title || subtitle) && (
            <div className="ui-modal__heading">
              {title && (
                <h2 id={titleId} className="ui-modal__title">
                  {title}
                </h2>
              )}
              {subtitle && <p className="ui-modal__subtitle">{subtitle}</p>}
            </div>
          )}
          <button type="button" className="ui-modal__close" onClick={onClose} aria-label={closeLabel}>
            <X aria-hidden="true" />
          </button>
        </div>
        <div className="ui-modal__body">{children}</div>
        {footer && <div className="ui-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
